<template>
  <div class="page-container">
    <div class="page-title-container d-flex justify-content-between align-items-center">
      <h1 class="page-title mb-0">Pagos de alquiler <small v-if="plate" class="text-muted">· {{ plate }}</small></h1>
      <div>
        <b-button variant="none" class="button button-light mr-2" :to="{ name: 'fleet-rentals' }">Volver</b-button>
        <b-button variant="none" class="button button-primary"
          :to="{ name: 'fleet-rental-payment-create', params: { vehicleId } }">
          <i class="ri-add-line mr-1"></i> Registrar pago
        </b-button>
      </div>
    </div>

    <div class="table-container">
      <div class="table-responsive mb-0 table-min-height">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th class="pl-3">Período</th>
              <th>Monto</th>
              <th>Estado</th>
              <th>Fecha de pago</th>
              <th>Método</th>
              <th>Comprobante</th>
              <th class="pr-md-4">Opciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in list" :key="o.id">
              <td class="text-center pl-3">{{ formatDate(o.period_start) }} – {{ formatDate(o.period_end) }}</td>
              <td class="text-center">{{ formatMoney(o.amount, o.currency) }}</td>
              <td class="text-center">
                <span :class="`badge ${o.payment_date ? 'badge-green' : 'badge-yellow'}`">
                  {{ o.payment_date ? 'Pagado' : 'Pendiente' }}
                </span>
              </td>
              <td class="text-center">{{ o.payment_date ? formatDate(o.payment_date) : '—' }}</td>
              <td class="text-center">{{ methodLabel(o.payment_method) }}</td>
              <td class="text-center">
                <button v-if="o.has_proof" type="button" class="link-button" title="Ver comprobante"
                  @click="openProof(o)"><i class="ri-image-line"></i></button>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center d_option pr-md-3">
                <DropdownTable>
                  <template slot="dropdown-content">
                    <DropdownItem text="Editar"
                      @click="$router.push({ name: 'fleet-rental-payment-edit', params: { id: o.id } })" />
                    <DropdownItem text="Eliminar" @click="openDelete(o)" />
                  </template>
                </DropdownTable>
              </td>
            </tr>
            <tr v-if="!list.length">
              <td colspan="7" class="text-center text-muted py-4">No se encontraron pagos.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="table-footer d-flex justify-content-end align-items-center">
        <pagination :total="total" :page="page" :page-size="pageSize" :pages="pages"
          :changePage="changePage" :changePageSize="changePageSize" />
      </div>
    </div>

    <b-modal v-model="deleteModal.show" title="Eliminar pago" hide-footer>
      <p>¿Está seguro que desea eliminar este pago? Esta acción no se puede deshacer.</p>
      <div class="text-right">
        <b-button variant="none" class="button button-light mr-2" @click="deleteModal.show = false">Cancelar</b-button>
        <b-button variant="none" class="button button-primary" @click="confirmDelete">Eliminar</b-button>
      </div>
    </b-modal>

    <b-modal v-model="proofModal.show" size="lg" hide-footer title="Comprobante de pago">
      <div class="photo-frame text-center">
        <img v-if="proofModal.url" :src="proofModal.url" alt="Comprobante" class="photo-img" />
        <p v-else-if="proofModal.error" class="text-muted m-0 py-4">{{ proofModal.error }}</p>
        <p v-else class="text-muted m-0 py-4">Cargando imagen…</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Alert, PaginationMixin, DropdownTable, DropdownItem } from 'wize-admin'
import { FleetRentalService, VehicleService } from '@/core/services'

const METHODS = {
  CASH: 'Efectivo', CARD: 'Tarjeta', TRANSFER: 'Transferencia',
  YAPE: 'Yape', PLIN: 'Plin', OTHER: 'Otro'
}

export default {
  name: 'RentalPaymentList',
  components: { DropdownTable, DropdownItem },
  mixins: [PaginationMixin],
  data() {
    return {
      list: [],
      plate: '',
      deleteModal: { show: false, id: null },
      proofModal: { show: false, url: null, error: null }
    }
  },
  computed: {
    vehicleId() { return Number(this.$route.params.vehicleId) }
  },
  methods: {
    methodLabel(m) { return m ? (METHODS[m] || m) : '—' },
    formatDate(value) {
      if (!value) return '—'
      const parts = String(value).slice(0, 10).split('-')
      if (parts.length !== 3) return value
      return `${parts[2]}/${parts[1]}/${parts[0]}`
    },
    formatMoney(v, currency) {
      const cur = currency || 'PEN'
      const sym = cur === 'PEN' ? 'S/' : cur
      return `${sym} ${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    async loadPlate() {
      try {
        const res = await VehicleService.get(this.vehicleId)
        this.plate = res.payload ? res.payload.license_plate || '' : ''
      } catch (e) { /* placa informativa */ }
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const res = await FleetRentalService.listPayments(this.vehicleId, { page: this.page, limit: this.pageSize })
        this.list = res.payload || []
        this.total = res.metadata ? res.metadata.totalElements : this.list.length
        this.pages = res.metadata ? res.metadata.totalPages : 1
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al listar los pagos')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    openDelete(o) { this.deleteModal = { show: true, id: o.id } },
    async confirmDelete() {
      try {
        await this.$store.dispatch('app/loading', true)
        await FleetRentalService.removePayment(this.deleteModal.id)
        this.deleteModal.show = false
        Alert.success('Pago eliminado correctamente')
        await this.loadData()
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'No se pudo eliminar el pago')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async openProof(o) {
      this.proofModal = { show: true, url: null, error: null }
      try {
        const res = await FleetRentalService.getProof(o.id)
        this.proofModal.url = res.payload ? res.payload.url : null
        if (!this.proofModal.url) this.proofModal.error = 'No hay comprobante disponible.'
      } catch (e) {
        console.error(e)
        this.proofModal.error = e.errors ? e.errors.message : 'No se pudo cargar el comprobante.'
      }
    }
  },
  async mounted() {
    await this.loadPlate()
    await this.loadData()
  }
}
</script>

<style lang="stylus" scoped>
.link-button
  background none
  border none
  color #007bff
  font-size 16px
  cursor pointer
  &:hover
    color #0056b3
.photo-frame
  background #f4f5f7
  border 1px solid #e5e7eb
  border-radius 8px
  min-height 220px
  display flex
  align-items center
  justify-content center
  overflow hidden
.photo-img
  max-width 100%
  max-height 70vh
  object-fit contain
</style>
